import { PhotoStudioAIMaskScope } from 'constants/visual/photoStudio'
import { PhotoStudioGenerationStepId } from '../generationSteps'
import { WallPaintingSettings } from 'dataQueries/photoStudio.query'
import { useCallback } from 'react'
import { logger } from 'utils/logging'
import { useWallPaintingProcessor } from './useWallPaintingProcessor.hook'

interface UseWallPaintingFlowProps {
  assignmentId: string
  sourceVisualFilename: string
  getLatestPreviewFilename: () => string | null
  onProgressUpdate?: (step: string) => void
  onComplete?: (resultUrl: string) => void
  onError?: (error: any) => void
}

/**
 * Complete Wall Painting flow hook
 * Handles processing and result retrieval
 * 
 * Usage:
 * 1. Call processWallPainting with settings
 * 2. Hook handles processing, polling, and result retrieval
 * 3. Calls onComplete with result URL when finished
 */
export const useWallPaintingFlow = ({
  assignmentId,
  sourceVisualFilename,
  getLatestPreviewFilename,
  onProgressUpdate,
  onComplete,
  onError
}: UseWallPaintingFlowProps) => {
  // Wall Painting processor
  const { processWallPainting, isProcessing, error } = useWallPaintingProcessor({
    assignmentId,
    sourceVisualFilename,
    getLatestPreviewFilename,
    onProgressUpdate,
    onComplete,
    onError
  })

  const processWallPaintingWithSettings = useCallback(async (
    settings: WallPaintingSettings,
    previousJobId?: string
  ): Promise<string> => {
    try {
      onProgressUpdate?.(PhotoStudioGenerationStepId.START_PROCESSING)

      // Process Wall Painting directly (no mask needed)
      const result = await processWallPainting(
        settings,
        true, // USE temp bucket for the Wall Painting
        previousJobId
      )

      if (result.success) {
        return result.jobId!
      } else {
        throw result.error
      }
    } catch (error) {
      logger.error('Wall Painting: Flow failed', error, {
        context: {
          component: 'PhotoStudio',
          action: 'wallPaintingFlow',
          metadata: { assignmentId }
        }
      })
      onError?.(error)
      throw error
    }
  }, [processWallPainting, onProgressUpdate, onError, assignmentId])

  return {
    processWallPaintingWithSettings,
    isProcessing,
    error
  }
}
